import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, User, Phone, MapPin, KeyRound, ArrowRight, Loader2, CheckCircle2, Briefcase, Factory } from "lucide-react";

const EMPTY = { business_name: "", owner_name: "", owner_phone: "", state: "", business_type: "", industry: "", gstin: "", email: "" };

const BUSINESS_TYPES = ["Retailer", "Wholesaler", "Distributor", "Stockist", "Super Stockist",
  "Manufacturer", "Trader", "Importer", "Exporter", "Service Provider", "Other"];
const INDUSTRIES = ["Electronics", "Electricals", "Hardware & Tools", "Plywood & Laminates", "FMCG",
  "Pharma", "Building Materials", "Textiles & Apparel", "Auto Parts", "Agriculture & Seeds",
  "Stationery", "Chemicals", "Food & Beverage", "Sanitary & Plumbing", "Paints", "Other"];

export default function Signup() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [f, setF] = useState(EMPTY);
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setVal = (k) => (v) => setF({ ...f, [k]: v });

  const startTrial = async () => {
    if (!f.business_name.trim() || !f.owner_name.trim() || !f.state.trim()) {
      toast.error("Please fill business name, your name and state");
      return;
    }
    if (f.owner_phone.replace(/\D/g, "").length < 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    if (!f.business_type) { toast.error("Select your business type"); return; }
    if (!f.industry) { toast.error("Select your industry"); return; }
    if (!f.gstin.trim()) { toast.error("GSTIN is required"); return; }
    if (!f.email.trim() || !f.email.includes("@")) { toast.error("Enter a valid email address"); return; }
    setLoading(true);
    try {
      await api.post("/auth/signup", f);
      const { data } = await api.post("/auth/request-otp", { phone: f.owner_phone });
      setDevOtp(data.dev_otp || "");
      setStep("otp");
      toast.success("Trial created! Enter the OTP to sign in.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { phone: f.owner_phone, code });
      login(data.token, data.user, data.tenant);
      navigate("/");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-full grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#101D51] text-white p-12 relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#F83A84]/20 blur-3xl" />
        <div className="absolute -left-16 bottom-10 w-72 h-72 rounded-full bg-[#0B7C5A]/20 blur-3xl" />
        <div className="flex items-center gap-2 relative">
          <img src="/tradedesk-mark.png" alt="TradeDesk360" className="w-9 h-9 rounded-lg bg-white p-1 object-contain" />
          <span className="font-bold text-xl">TradeDesk360</span>
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#0B7C5A]/25 border border-[#0B7C5A]/40 px-3 py-1 text-xs font-semibold text-emerald-200 mb-5">
            <CheckCircle2 className="w-3.5 h-3.5" /> 15-day free trial · no card required
          </div>
          <h1 className="text-4xl font-extrabold leading-tight">Start selling from<br />your WhatsApp today.</h1>
          <p className="text-white/70 mt-4 max-w-md">Create your shop account in under a minute. Turn customer chats into enquiries, quotes, orders and GST invoices.</p>
          <ul className="mt-8 space-y-2.5 text-sm text-white/80">
            {["Full access for 15 days", "GST-ready invoices & PDFs", "WhatsApp inbox & enquiry desk"].map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-300" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="text-white/40 text-xs relative">A Clotus Infotech product</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/tradedesk-logo.png" alt="TradeDesk360" className="h-9 object-contain" />
          </div>

          {step === "form" ? (
            <>
              <h2 className="text-2xl font-bold text-[#101D51]">Start your free trial</h2>
              <p className="text-sm text-[#6B7189] mt-1 mb-6">15 days free. Set up your shop in a minute.</p>
              <div className="space-y-3.5">
                <Field icon={Building2} label="Business name" value={f.business_name} onChange={set("business_name")} placeholder="e.g. Sharma Electricals" testid="signup-business-name" />
                <div className="grid grid-cols-2 gap-3">
                  <Field icon={User} label="Your name" value={f.owner_name} onChange={set("owner_name")} placeholder="Full name" testid="signup-owner-name" />
                  <Field icon={MapPin} label="State" value={f.state} onChange={set("state")} placeholder="e.g. Bihar" testid="signup-state" />
                </div>
                <Field icon={Phone} label="Mobile number" value={f.owner_phone} onChange={set("owner_phone")} placeholder="9876500001" inputMode="numeric" testid="signup-phone" />
                <div className="grid grid-cols-2 gap-3">
                  <SelectField icon={Briefcase} label="I am a..." value={f.business_type} onValueChange={setVal("business_type")} options={BUSINESS_TYPES} placeholder="Business type" testid="signup-business-type" />
                  <SelectField icon={Factory} label="Industry" value={f.industry} onValueChange={setVal("industry")} options={INDUSTRIES} placeholder="Select industry" testid="signup-industry" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="GSTIN" value={f.gstin} onChange={set("gstin")} placeholder="15-digit GSTIN" testid="signup-gstin" />
                  <Field label="Email" value={f.email} onChange={set("email")} placeholder="you@shop.com" inputMode="email" testid="signup-email" />
                </div>
                <Button data-testid="signup-submit-btn" onClick={startTrial} disabled={loading}
                  className="w-full h-11 bg-[#F83A84] hover:bg-[#e02f74]">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start free trial <ArrowRight className="w-4 h-4" /></>}
                </Button>
                <p className="text-sm text-center text-[#6B7189]">
                  Already have an account? <Link to="/login" className="font-semibold text-[#101D51] hover:underline" data-testid="signup-to-login">Sign in</Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-[#101D51]">Verify your number</h2>
              <p className="text-sm text-[#6B7189] mt-1 mb-6">We sent a code to +91 {f.owner_phone}.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-[#6B7189]">One-time password</label>
                  <div className="relative mt-1">
                    <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />
                    <Input data-testid="signup-otp-input" value={code} inputMode="numeric" maxLength={6}
                      onChange={(e) => setCode(e.target.value)} placeholder="123456"
                      className="pl-9 h-11 tracking-[0.4em] font-semibold" onKeyDown={(e) => e.key === "Enter" && verify()} />
                  </div>
                </div>
                {devOtp && (
                  <div className="text-xs bg-[#F5F6FA] border border-[#E6E8F0] rounded-lg px-3 py-2 text-[#6B7189]">
                    Demo mode — your OTP is <span className="font-bold text-[#101D51]">{devOtp}</span>
                  </div>
                )}
                <Button data-testid="signup-verify-btn" onClick={verify} disabled={loading || code.length < 4}
                  className="w-full h-11 bg-[#0B7C5A] hover:bg-[#09664a]">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & enter TradeDesk"}
                </Button>
                <button onClick={() => setStep("form")} className="text-sm text-[#6B7189] hover:text-[#101D51] w-full text-center">
                  Back
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, testid, ...rest }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#6B7189]">{label}</label>
      <div className="relative mt-1">
        {Icon && <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189]" />}
        <Input data-testid={testid} className={`h-11 ${Icon ? "pl-9" : ""}`} {...rest} />
      </div>
    </div>
  );
}

function SelectField({ icon: Icon, label, value, onValueChange, options, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#6B7189]">{label}</label>
      <div className="relative mt-1">
        {Icon && <Icon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7189] z-10 pointer-events-none" />}
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger data-testid={testid} className={`h-11 ${Icon ? "pl-9" : ""}`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {options.map((o) => (
              <SelectItem key={o} value={o} data-testid={`${testid}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
